import { useCallback, useState } from "react";
import { useApp } from "@/lib/AppContext";
import type { ApiClient, StepArtifact } from "@/lib/api";
import { apiProblem, useLoaded } from "@/lib/useLoaded";

const problem = apiProblem("The artifacts could not be read.");

/**
 * Artifacts read once, the first time `open` turns true (ARCH-8), and kept after it closes.
 *
 * Both the Debug tab and the processing panel's dev-mode expansion open one step at a time
 * and want this "don't fetch until asked" behaviour, for the same reason: a reconciled
 * transcript is tens of thousands of words, and loading every step's for a list of rows
 * nobody has opened would make whichever screen does it the slowest thing in the app.
 */
function useArtifactsWhenOpened(read: (api: ApiClient) => Promise<StepArtifact[]>, open: boolean) {
  const { api } = useApp();

  // Latched: once asked for, the artifacts stay loaded however often the row is folded.
  const [wanted, setWanted] = useState(open);
  if (open && !wanted) setWanted(true);

  const { value, problem: failure } = useLoaded<StepArtifact[] | null>(api, read, null, problem, {
    enabled: wanted,
  });

  return { artifacts: value, problem: failure };
}

/** A step's artifacts, reached through its recording. */
export function useStepArtifacts(recordingId: string, stepId: string, open: boolean) {
  const read = useCallback(
    (api: ApiClient) => api.getStepArtifacts(recordingId, stepId),
    [recordingId, stepId],
  );

  return useArtifactsWhenOpened(read, open);
}

/**
 * UI-24 — a step's artifacts reached through its synthesis batch, because a synthesis run has
 * no source to read them through (`SynthesisBatchEndpoints`, the mirror of
 * `PipelineEndpoints`'s own route).
 */
export function useBatchStepArtifacts(batchId: string, stepId: string, open: boolean) {
  const read = useCallback(
    (api: ApiClient) => api.getBatchStepArtifacts(batchId, stepId),
    [batchId, stepId],
  );

  return useArtifactsWhenOpened(read, open);
}
